import { supabase } from '@/services/supabase';
import {
  SearchHistoryEntry,
  StockSearchSummary,
  CreateSearchHistoryInput,
  Message,
} from '@/types/search-history.types';

/**
 * Save a search to history
 */
export const saveSearchHistory = async (
  input: CreateSearchHistoryInput
): Promise<{ success: boolean; entry?: SearchHistoryEntry; error?: string }> => {
  try {
    console.log(
      `💾 [SearchHistoryService] Saving search: "${input.query}" ${input.stock_symbol ? `(${input.stock_symbol})` : ''}`
    );
    const startTime = Date.now();

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error(`❌ [SearchHistoryService] Cannot save - user not authenticated`);
      return { success: false, error: 'User not authenticated' };
    }

    console.log(`👤 [SearchHistoryService] Saving for user: ${user.id}`);
    console.log(`📊 [SearchHistoryService] Messages count: ${input.messages?.length || 0}`);

    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('search_history')
      .insert({
        user_id: user.id,
        query: input.query,
        stock_symbol: input.stock_symbol || null,
        stock_name: input.stock_name || null,
        ai_response: input.ai_response || null,
        stock_data: input.stock_data || null,
        messages: input.messages || [], // NEW: Save messages array
        created_at: now,
        updated_at: now,
      })
      .select()
      .single();

    if (error) {
      console.error(`❌ [SearchHistoryService] Error saving search history:`, error);
      return { success: false, error: error.message };
    }

    const saveTime = Date.now() - startTime;
    console.log(`✅ [SearchHistoryService] Saved search history in ${saveTime}ms (ID: ${data.id})`);

    return { success: true, entry: data };
  } catch (error) {
    console.error(`❌ [SearchHistoryService] Unexpected error saving search history:`, error);
    return { success: false, error: 'Failed to save search history' };
  }
};

/**
 * Get stock search summary for dropdown
 * Groups searches by stock symbol, shows most recent for each
 */
export const getStockSearchSummary = async (): Promise<{
  success: boolean;
  summaries?: StockSearchSummary[];
  error?: string;
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get all stock searches for this user, ordered by most recent
    const { data, error } = await supabase
      .from('search_history')
      .select('*')
      .eq('user_id', user.id)
      .not('stock_symbol', 'is', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching stock search summary:', error);
      return { success: false, error: error.message };
    }

    if (!data || data.length === 0) {
      return { success: true, summaries: [] };
    }

    // Group by stock_symbol and get summary
    const stockMap = new Map<string, StockSearchSummary>();

    data.forEach((entry: SearchHistoryEntry) => {
      if (!entry.stock_symbol) return;

      const existing = stockMap.get(entry.stock_symbol);

      if (!existing) {
        stockMap.set(entry.stock_symbol, {
          stock_symbol: entry.stock_symbol,
          stock_name: entry.stock_name || entry.stock_symbol,
          search_count: 1,
          last_searched_at: entry.created_at,
          last_query: entry.query,
          last_entry: entry,
        });
      } else {
        existing.search_count += 1;
      }
    });

    // Convert to array and sort by most recent
    const summaries = Array.from(stockMap.values()).sort(
      (a, b) => new Date(b.last_searched_at).getTime() - new Date(a.last_searched_at).getTime()
    );

    return { success: true, summaries };
  } catch (error) {
    console.error('Unexpected error fetching stock search summary:', error);
    return { success: false, error: 'Failed to fetch search history' };
  }
};

/**
 * Get most recent search for a specific stock
 */
export const getLatestStockSearch = async (
  stockSymbol: string
): Promise<{ success: boolean; entry?: SearchHistoryEntry; error?: string }> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('search_history')
      .select('*')
      .eq('user_id', user.id)
      .eq('stock_symbol', stockSymbol)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      // Not finding an entry is not necessarily an error
      if (error.code === 'PGRST116') {
        return { success: true, entry: undefined };
      }
      console.error('Error fetching latest stock search:', error);
      return { success: false, error: error.message };
    }

    return { success: true, entry: data };
  } catch (error) {
    console.error('Unexpected error fetching latest stock search:', error);
    return { success: false, error: 'Failed to fetch stock search' };
  }
};

/**
 * Get full conversation history for a stock across all search sessions
 */
export const getFullStockConversationHistory = async (
  stockSymbol: string
): Promise<{ success: boolean; messages?: Message[]; error?: string }> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Call the database function to get all messages across sessions
    const { data, error } = await supabase.rpc('get_stock_conversation_history', {
      p_user_id: user.id,
      p_stock_symbol: stockSymbol,
    });

    if (error) {
      console.error('Error fetching full conversation history:', error);
      return { success: false, error: error.message };
    }

    if (!data || data.length === 0) {
      return { success: true, messages: [] };
    }

    // Convert database result to Message format
    const messages: Message[] = data.map((row: any) => ({
      id: row.message_id,
      role: row.role as 'user' | 'assistant',
      content: row.content,
      timestamp: row.message_timestamp, // Updated to use correct column name
      citations: row.citations || [],
    }));

    return { success: true, messages };
  } catch (error) {
    console.error('Unexpected error fetching full conversation history:', error);
    return { success: false, error: 'Failed to fetch conversation history' };
  }
};

/**
 * Get conversation summary for dropdown (updated to use new function)
 */
export const getStockConversationSummary = async (): Promise<{
  success: boolean;
  summaries?: StockSearchSummary[];
  error?: string;
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Call the database function to get conversation summaries
    const { data, error } = await supabase.rpc('get_stock_conversation_summary', {
      p_user_id: user.id,
    });

    if (error) {
      console.error('Error fetching conversation summary:', error);
      return { success: false, error: error.message };
    }

    if (!data || data.length === 0) {
      return { success: true, summaries: [] };
    }

    // Convert to StockSearchSummary format
    const summaries: StockSearchSummary[] = data.map((row: any) => ({
      stock_symbol: row.stock_symbol,
      stock_name: row.stock_name,
      search_count: row.search_count,
      last_searched_at: row.last_message_timestamp,
      last_query: row.last_message_content,
      last_entry: {
        id: row.last_search_id,
        user_id: user.id,
        query: row.last_message_content,
        stock_symbol: row.stock_symbol,
        stock_name: row.stock_name,
        ai_response: null,
        stock_data: null,
        messages: [],
        created_at: row.last_message_timestamp,
        updated_at: row.last_message_timestamp,
      },
    }));

    return { success: true, summaries };
  } catch (error) {
    console.error('Unexpected error fetching conversation summary:', error);
    return { success: false, error: 'Failed to fetch conversation summary' };
  }
};

/**
 * Append message to existing search history
 */
export const appendMessageToHistory = async (
  stockSymbol: string,
  message: Message
): Promise<{ success: boolean; error?: string }> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get latest entry for this stock
    const { data: latestEntry, error: fetchError } = await supabase
      .from('search_history')
      .select('*')
      .eq('user_id', user.id)
      .eq('stock_symbol', stockSymbol)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (fetchError || !latestEntry) {
      console.error('Error fetching latest entry:', fetchError);
      return { success: false, error: 'Entry not found' };
    }

    // Append new message to messages array
    const updatedMessages = [...(latestEntry.messages || []), message];

    // Update the entry
    const { error: updateError } = await supabase
      .from('search_history')
      .update({
        messages: updatedMessages,
        updated_at: new Date().toISOString(),
      })
      .eq('id', latestEntry.id);

    if (updateError) {
      console.error('Error updating messages:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error appending message:', error);
    return { success: false, error: 'Failed to append message' };
  }
};

/**
 * Delete old search history entries
 * Keeps only last 100 entries per user
 */
export const cleanupOldHistory = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get IDs of entries to keep (most recent 100)
    const { data: recentEntries, error: fetchError } = await supabase
      .from('search_history')
      .select('id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);

    if (fetchError) {
      console.error('Error fetching recent entries:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!recentEntries || recentEntries.length === 0) {
      return { success: true };
    }

    const idsToKeep = recentEntries.map(e => e.id);

    // Delete entries not in the keep list
    const { error: deleteError } = await supabase
      .from('search_history')
      .delete()
      .eq('user_id', user.id)
      .not('id', 'in', `(${idsToKeep.join(',')})`);

    if (deleteError) {
      console.error('Error deleting old entries:', deleteError);
      return { success: false, error: deleteError.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error cleaning up history:', error);
    return { success: false, error: 'Failed to cleanup history' };
  }
};
